/**
 * Gestione del token JWT con fallback in-memory
 * Versione semplificata per risolvere problemi di importazioni circolari
 * 
 * ISOLAMENTO PER UTENTE: Ogni utente Telegram ha il proprio token salvato
 * con una chiave specifica basata sul telegramId per evitare conflitti
 */

// Costanti
const TOKEN_KEY_PREFIX = 'auth_token'; // Prefisso base per le chiavi
const LEGACY_TOKEN_KEY = 'AUTH_TOKEN'; // Vecchia chiave per compatibilità
const TOKEN_KEY = 'authToken'; // Chiave specifica per l'app attuale

// Memoria in-memory per ambienti senza localStorage
let inMemoryToken: string | null = null;
let cachedToken: string | null = null;
let currentTelegramId: string | null = null; // TelegramId dell'utente corrente

/**
 * Costruisce la chiave di storage specifica per l'utente
 */
function getTokenKey(telegramId?: string | null): string {
  const id = telegramId || currentTelegramId;
  if (id) {
    return `${TOKEN_KEY_PREFIX}_${id}`;
  }
  // Fallback alla chiave legacy se non c'è telegramId
  return LEGACY_TOKEN_KEY;
}

/**
 * Imposta il telegramId dell'utente corrente
 */
export function setCurrentTelegramId(telegramId: string | null): void {
  console.log('[TOKEN STORAGE] Setting current telegramId:', telegramId);
  currentTelegramId = telegramId;
}

// Implementazione semplificata per verificare la disponibilità di localStorage
function canUseLocalStorage(): boolean {
  if (typeof window === 'undefined') return false;
  if (!window.localStorage) return false;

  try {
    // Test di base
    const test = '__test__';
    window.localStorage.setItem(test, test);
    window.localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

// Implementazione semplificata per verificare la disponibilità di sessionStorage
function canUseSessionStorage(): boolean {
  if (typeof window === 'undefined') return false;
  if (!window.sessionStorage) return false;

  try {
    // Test di base
    const test = '__test__';
    window.sessionStorage.setItem(test, test);
    window.sessionStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}


// Salva token (in localStorage se disponibile o in memoria)
export function saveToken(token: string, telegramId?: string | null): boolean {
  console.log('[TOKEN STORAGE] Tentativo di salvare token JWT...');

  // Aggiorna il telegramId corrente se fornito
  if (telegramId) {
    currentTelegramId = telegramId;
  }

  if (!token) {
    console.error('[TOKEN STORAGE] Errore: tentativo di salvare un token vuoto o null');
    return false;
  }

  // Salva sempre in memoria
  inMemoryToken = token;
  cachedToken = token;
  console.log('[TOKEN STORAGE] Token salvato in memoria.');

  // Tenta di salvare in localStorage con chiave specifica per utente
  const canUseLS = canUseLocalStorage();
  console.log('[TOKEN STORAGE] localStorage disponibile:', canUseLS);

  let lsSuccess = false;
  if (canUseLS) {
    try {
      const tokenKey = getTokenKey(telegramId);
      console.log('[TOKEN STORAGE] Usando chiave storage:', tokenKey);

      window.localStorage.setItem(tokenKey, token);
      // Salva anche con chiave universale per il web (dopo reload il telegramId si perde)
      window.localStorage.setItem(TOKEN_KEY, token);
      console.log('[TOKEN STORAGE] Token salvato in localStorage con chiave specifica per utente e chiave universale.');

      // Doppia verifica
      try {
        const storedToken = window.localStorage.getItem(tokenKey);
        if (storedToken) {
          console.log('[TOKEN STORAGE] Verifica: token trovato in localStorage.');
          lsSuccess = true;
        } else {
          console.warn('[TOKEN STORAGE] PROBLEMA: token NON trovato in localStorage dopo il salvataggio!');

          // Ritenta una volta
          window.localStorage.setItem(tokenKey, token);
          const retryToken = window.localStorage.getItem(tokenKey);
          lsSuccess = !!retryToken;

          console.log('[TOKEN STORAGE] Secondo tentativo di salvataggio:', lsSuccess ? 'RIUSCITO' : 'FALLITO');
        }
      } catch (verifyErr) {
        console.error('[TOKEN STORAGE] Errore nella verifica:', verifyErr);
      }
    } catch (err) {
      console.error('[TOKEN STORAGE] Errore durante il salvataggio in localStorage:', err);
      console.warn('[TOKEN STORAGE] Utilizziamo solo storage in-memory per il token');
    }
  }

  return lsSuccess || !!inMemoryToken;
}

// Helper function to check if localStorage is available
function isLocalStorageAvailable(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const testKey = '__storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

// Recupera token da tutte le possibili fonti (localStorage con chiave per utente, memoria)
export function getToken(telegramId?: string | null): string | null {
  console.log('[TOKEN STORAGE] Tentativo di recuperare token JWT...');

  // Aggiorna il telegramId corrente se fornito
  if (telegramId) {
    currentTelegramId = telegramId;
  }

  try {
    let token: string | null = null;

    // 1. Prova prima la memoria in-memory (più veloce)
    if (inMemoryToken) {
      console.log('[TOKEN STORAGE] Token trovato in memoria in-memory');
      return inMemoryToken;
    }

    // 2. Prova localStorage con chiave specifica per utente
    if (isLocalStorageAvailable()) {
      const tokenKey = getTokenKey(telegramId);
      console.log('[TOKEN STORAGE] Cercando token con chiave:', tokenKey);

      token = localStorage.getItem(tokenKey);
      if (token) {
        console.log('[TOKEN STORAGE] ✅ Token trovato in localStorage con chiave specifica per utente');
        // Salva in memoria per accesso rapido
        inMemoryToken = token;
        cachedToken = token;
        return token;
      }

      // 2b. Prova chiave universale "authToken"
      if (!token) {
        token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          console.log('[TOKEN STORAGE] ✅ Token trovato con chiave universale:', TOKEN_KEY);
          inMemoryToken = token;
          cachedToken = token;
          return token;
        }
      }

      // 3. MIGRAZIONE: Se non trovato con chiave specifica, prova chiave legacy
      if (!token && tokenKey !== LEGACY_TOKEN_KEY) {
        console.log('[TOKEN STORAGE] Cercando token legacy...');
        token = localStorage.getItem(LEGACY_TOKEN_KEY);
        if (token) {
          console.log('[TOKEN STORAGE] ⚠️ Token trovato con chiave legacy, migrazione in corso...');

          // Migra alla nuova chiave
          if (currentTelegramId) {
            localStorage.setItem(tokenKey, token);
            localStorage.removeItem(LEGACY_TOKEN_KEY);
            console.log('[TOKEN STORAGE] ✅ Token migrato alla nuova chiave:', tokenKey);
          }

          // Salva in memoria
          inMemoryToken = token;
          cachedToken = token;
          return token;
        }
      }
    }

    // 4. FALLBACK: Prova a recuperare dal cookie (per utenti con problemi di storage)
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === TOKEN_KEY) { // Usiamo la chiave specifica dell'app
          console.log('[TOKEN STORAGE] Token trovato nei cookies (fallback)');
          inMemoryToken = value; // Cache in memoria
          cachedToken = value;
          return value;
        }
      }
    }


    if (!token) {
      console.log('[TOKEN STORAGE] Nessun token trovato');
      return null;
    }

    return token;
  } catch (error) {
    console.error('[TOKEN STORAGE] Errore durante il recupero del token:', error);
    return null;
  }
};

// Rimuovi token da tutte le fonti
export function removeToken(telegramId?: string | null): void {
  console.log('[TOKEN STORAGE] Rimozione token...');

  // Aggiorna il telegramId corrente se fornito
  if (telegramId) {
    currentTelegramId = telegramId;
  }

  // 1. Rimuovi dalla memoria
  inMemoryToken = null;
  cachedToken = null;

  // 2. Rimuovi da localStorage con chiave specifica per utente
  if (canUseLocalStorage()) {
    try {
      const tokenKey = getTokenKey(telegramId);
      console.log('[TOKEN STORAGE] Rimuovendo token con chiave:', tokenKey);

      window.localStorage.removeItem(tokenKey);

      // Rimuovi anche la chiave legacy se diversa
      if (tokenKey !== LEGACY_TOKEN_KEY) {
        window.localStorage.removeItem(LEGACY_TOKEN_KEY);
      }

      console.log('[TOKEN STORAGE] ✅ Token rimosso da localStorage');
    } catch (err) {
      console.warn('[TOKEN STORAGE] Errore nella rimozione da localStorage:', err);
    }
  }

  // 3. Rimuovi da sessionStorage se possibile
  if (canUseSessionStorage()) {
    try {
      window.sessionStorage.removeItem(TOKEN_KEY);
      window.sessionStorage.removeItem('auth_token');
      window.sessionStorage.removeItem('AUTH_TOKEN');
      window.sessionStorage.removeItem('authToken');
      console.log('[TOKEN STORAGE] Token rimosso da sessionStorage');
    } catch (err) {
      console.warn('[TOKEN STORAGE] Errore nella rimozione da sessionStorage:', err);
    }
  }

  // 4. Rimuovi dai cookie - tutte le varianti
  try {
    const cookieNames = [TOKEN_KEY, 'auth_token', 'AUTH_TOKEN', 'authToken'];
    cookieNames.forEach(name => {
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict`;
    });
    console.log('[TOKEN STORAGE] Token rimosso dai cookie (tutte le varianti)');
  } catch (cookieErr) {
    console.warn('[TOKEN STORAGE] Errore nella rimozione dai cookie:', cookieErr);
  }

  // 5. Rimuovi dalla memoria globale se presente
  if (typeof window !== 'undefined') {
    try {
      delete (window as any).__authToken;
      console.log('[TOKEN STORAGE] Token rimosso dalla memoria globale');
    } catch (memErr) {
      console.warn('[TOKEN STORAGE] Errore nella rimozione dalla memoria globale:', memErr);
    }
  }

  console.log('[TOKEN STORAGE] Rimozione token completata');
}

/**
 * Verifica se esiste un token
 * @returns true se esiste un token, false altrimenti
 */
export const hasToken = (): boolean => {
  try {
    return !!getToken();
  } catch (error) {
    console.error('Errore durante la verifica del token:', error);
    return false;
  }
};

/**
 * Aggiunge il token JWT all'header Authorization di una richiesta fetch
 * @param options Opzioni fetch
 * @returns Opzioni fetch con token JWT
 */
export const withAuth = (options: RequestInit = {}): RequestInit => {
  const token = getToken();

  if (!token) {
    return {
      ...options,
      credentials: 'include' as RequestCredentials
    };
  }

  const authHeader = `Bearer ${token}`;

  // Unisce gli headers esistenti con l'header Authorization
  const headers = {
    ...options.headers,
    'Authorization': authHeader
  };

  return {
    ...options,
    headers,
    credentials: 'include' as RequestCredentials
  };
};

// getToken è già esportata sopra come function export

// Alias per compatibilità con il codice esistente
export const clearToken = (): void => {
  console.log('[TOKEN STORAGE] Rimozione token JWT...');

  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.removeItem(TOKEN_KEY);
    console.log('[TOKEN STORAGE] Token rimosso da localStorage');
  }

  // Pulisci anche la memoria
  cachedToken = null;
};

// Funzione per verificare se un token è ancora valido chiamando l'API
export const validateTokenWithServer = async (): Promise<boolean> => {
  const token = getToken();
  if (!token) return false;

  try {
    const response = await fetch('/api/auth/me', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.status === 401) {
      console.log('[TOKEN STORAGE] Token non valido o utente inesistente, rimozione automatica');
      removeToken();

      // Non fare reload automatico per evitare loop infiniti
      // Lascia che l'app gestisca lo stato tramite useAuth
      console.log('[TOKEN STORAGE] Token obsoleto rimosso, delegando gestione stato a useAuth');

      return false;
    }

    return response.ok;
  } catch (error) {
    console.error('[TOKEN STORAGE] Errore validazione token:', error);
    return false;
  }
};

export const getAuthToken = (): string | null => {
  console.log('[TOKEN STORAGE] Tentativo di recuperare token JWT...');
  console.log('[TOKEN STORAGE] localStorage disponibile:', typeof localStorage !== 'undefined');

  try {
    // Prima prova localStorage
    if (typeof localStorage !== 'undefined') {
      const token = localStorage.getItem(TOKEN_KEY); // Usiamo la chiave specifica dell'app
      if (token && token.trim() !== '' && token !== 'null' && token !== 'undefined') {
        console.log('[TOKEN STORAGE] Token valido recuperato da localStorage:', `${token.substring(0, 15)}...`);
        return token;
      }
    }

    // Fallback ai cookie se localStorage non funziona
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === TOKEN_KEY && value && value.trim() !== '' && value !== 'null' && value !== 'undefined') {
          console.log('[TOKEN STORAGE] Token recuperato dai cookie come fallback');
          // Ripristina in localStorage se possibile
          if (typeof localStorage !== 'undefined') {
            try {
              localStorage.setItem(TOKEN_KEY, value); // Usiamo la chiave specifica dell'app
              console.log('[TOKEN STORAGE] Token ripristinato in localStorage');
            } catch (e) {
              console.warn('[TOKEN STORAGE] Impossibile salvare token in localStorage:', e);
            }
          }
          return value;
        }
      }
    }

    // Ultimo tentativo: controlla se c'è un token in memoria (per utenti con problemi di storage)
    if (typeof window !== 'undefined' && (window as any).__authToken) {
      const memoryToken = (window as any).__authToken;
      console.log('[TOKEN STORAGE] Token recuperato dalla memoria come ultimo fallback');
      // Tenta di ripristinarlo
      if (typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem(TOKEN_KEY, memoryToken); // Usiamo la chiave specifica dell'app
        } catch (e) {
          console.warn('[TOKEN STORAGE] Impossibile salvare token in memoria in localStorage:', e);
        }
      }
      return memoryToken;
    }

    console.log('[TOKEN STORAGE] Nessun token valido trovato in nessuna location');
    return null;
  } catch (error) {
    console.error('[TOKEN STORAGE] Errore nel recupero token:', error);
    return null;
  }
};

export const saveAuthToken = (token: string): void => {
  console.log('[TOKEN STORAGE] Tentativo di salvare token JWT...');

  try {
    // Salva in memoria come fallback primario
    if (typeof window !== 'undefined') {
      (window as any).__authToken = token;
      console.log('[TOKEN STORAGE] Token salvato in memoria.');
    }

    // Salva nei cookie come fallback aggiuntivo
    if (typeof document !== 'undefined') {
      const expires = new Date();
      expires.setDate(expires.getDate() + 30); // 30 giorni
      document.cookie = `${TOKEN_KEY}=${token}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`; // Usiamo la chiave specifica dell'app
      console.log('[TOKEN STORAGE] Token salvato anche in cookie come fallback.');
    }

    // Salva in localStorage come storage principale
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(TOKEN_KEY, token); // Usiamo la chiave specifica dell'app
        console.log('[TOKEN STORAGE] Token salvato in localStorage.');

        // Verifica che sia stato salvato correttamente
        const savedToken = localStorage.getItem(TOKEN_KEY);
        if (savedToken === token) {
          console.log('[TOKEN STORAGE] Verifica: token trovato in localStorage.');
        } else {
          console.error('[TOKEN STORAGE] ERRORE: token non salvato correttamente in localStorage!');
          console.log('[TOKEN STORAGE] Tentativo di recupero da memoria...');
          // Se localStorage fallisce, assicurati che almeno la memoria funzioni
          if (typeof window !== 'undefined') {
            (window as any).__authToken = token;
          }
        }
      } catch (storageError) {
        console.error('[TOKEN STORAGE] Errore specifico localStorage:', storageError);
        console.log('[TOKEN STORAGE] Utilizzando solo memoria e cookie come fallback.');
      }
    } else {
      console.warn('[TOKEN STORAGE] localStorage non disponibile, usando memoria e cookie.');
    }
  } catch (error) {
    console.error('[TOKEN STORAGE] Errore nel salvataggio token:', error);
  }
};

// Funzione per validare se il token è scaduto
export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);

    if (payload.exp && payload.exp < currentTime) {
      console.log('[TOKEN STORAGE] Token scaduto automaticamente, rimozione...');
      console.log('[TOKEN STORAGE] Token scadenza:', new Date(payload.exp * 1000).toLocaleString());
      console.log('[TOKEN STORAGE] Ora attuale:', new Date().toLocaleString());
      removeToken();

      // Forza il refresh della pagina se siamo appena registrati per evitare stati inconsistenti
      const justRegistered = sessionStorage.getItem('just_registered') === 'true';
      if (justRegistered) {
        console.log('[TOKEN STORAGE] Token scaduto durante just_registered, refresh pagina...');
        setTimeout(() => window.location.reload(), 100);
      }

      return true;
    }

    return false;
  } catch (error) {
    console.error('[TOKEN STORAGE] Errore nella validazione del token:', error);
    removeToken();
    return true;
  }
}